import React, { useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast
} from '@chakra-ui/react'
import startCase from 'lodash/startCase'

import axios from 'lib/axios'
import { formatMoney } from 'lib/numbers'
import type { RentRollLedgerDataQuery } from 'graphql/generated'

type LedgerRental = NonNullable<RentRollLedgerDataQuery['rentals_by_pk']>
type ScheduledCharge = LedgerRental['scheduled_rental_liabilities'][number]

const canCancelSchedule = (canCancel: boolean, schedule: ScheduledCharge) =>
  canCancel && schedule.source === 'manual' && ['scheduled', 'blocked'].includes(schedule.status)

const canResolveSchedule = (canResolve: boolean, schedule: ScheduledCharge) =>
  canResolve && schedule.status === 'blocked' && Boolean(schedule.renewal_id)

const statusColor = (status: string) => {
  if (status === 'posted') return 'green'
  if (status === 'blocked') return 'red'
  if (status === 'scheduled') return 'blue'
  if (status === 'contingent') return 'yellow'
  return 'gray'
}

const sourceLabel = (source: string) =>
  source === 'kcpoa_initial_option_fee' ? 'RPP initial option fee' : startCase(source)

const terminalStatuses = new Set(['canceled', 'posted', 'waived'])

export default function ScheduledChargesTable({
  canCancel,
  canResolve,
  onChange,
  schedules
}: {
  canCancel: boolean
  canResolve: boolean
  onChange: () => Promise<void>
  schedules: ScheduledCharge[]
}) {
  const [workingId, setWorkingId] = useState<string>()
  const toast = useToast()
  const visibleSchedules = schedules.filter((schedule) => !terminalStatuses.has(schedule.status))
  const showActions = visibleSchedules.some(
    (schedule) => canCancelSchedule(canCancel, schedule) || canResolveSchedule(canResolve, schedule)
  )

  if (!visibleSchedules.length) return null

  const runAction = async (schedule: ScheduledCharge, action: 'cancel' | 'retry' | 'waive') => {
    const reason = window.prompt(`Why should this scheduled charge be ${action}ed?`)?.trim()
    if (!reason) return

    setWorkingId(schedule.id)
    try {
      if (action === 'cancel') {
        await axios.delete(`/admin/scheduled_rental_liabilities/${schedule.id}`, {
          data: { reason }
        })
      } else {
        await axios.post(`/admin/scheduled_rental_liabilities/${schedule.id}/resolve`, {
          action,
          reason
        })
      }
      toast({
        title: `Scheduled charge ${action === 'cancel' ? 'canceled' : `${action} authorized`}`,
        status: 'success',
        isClosable: true
      })
      await onChange()
    } catch (error: any) {
      toast({
        title: 'Unable to update scheduled charge',
        description: error?.response?.data?.message ?? error.message,
        status: 'error',
        isClosable: true
      })
    } finally {
      setWorkingId(undefined)
    }
  }

  return (
    <Box mt={5}>
      <Heading as='h3' fontSize='md' mb={2}>
        Scheduled charges
      </Heading>
      <TableContainer border='1px solid' borderColor='gray.200' borderRadius='sm'>
        <Table size='sm' variant='simple'>
          <Thead bg='gray.50'>
            <Tr>
              <Th>Scheduled date</Th>
              <Th>Charge</Th>
              <Th isNumeric>Amount</Th>
              <Th>Status</Th>
              <Th>Explanation</Th>
              {showActions && <Th textAlign='right'>Actions</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {visibleSchedules.map((schedule) => {
              const scheduleCanBeCanceled = canCancelSchedule(canCancel, schedule)
              const scheduleCanBeResolved = canResolveSchedule(canResolve, schedule)

              return (
                <Tr key={schedule.id}>
                  <Td>{schedule.due_date}</Td>
                  <Td>
                    <Text>{schedule.liability_type.name}</Text>
                    <Text color='gray.600' fontSize='xs'>
                      {sourceLabel(schedule.source)}
                    </Text>
                  </Td>
                  <Td isNumeric>{formatMoney(Number(schedule.amount), 2, '$')}</Td>
                  <Td>
                    <Badge colorScheme={statusColor(schedule.status)} textTransform='none'>
                      {startCase(schedule.status)}
                    </Badge>
                  </Td>
                  <Td maxW='360px' whiteSpace='normal'>
                    <Text>{schedule.note}</Text>
                    {schedule.last_error && (
                      <Text color='red.600' fontSize='xs' mt={1}>
                        {schedule.last_error}
                      </Text>
                    )}
                  </Td>
                  {showActions && (
                    <Td>
                      <HStack justify='flex-end'>
                        {scheduleCanBeResolved && (
                          <>
                            <Button
                              isLoading={workingId === schedule.id}
                              onClick={() => runAction(schedule, 'retry')}
                              size='xs'
                              variant='outline'
                            >
                              Retry
                            </Button>
                            <Button
                              colorScheme='orange'
                              isLoading={workingId === schedule.id}
                              onClick={() => runAction(schedule, 'waive')}
                              size='xs'
                              variant='outline'
                            >
                              Waive
                            </Button>
                          </>
                        )}
                        {scheduleCanBeCanceled && (
                          <Button
                            colorScheme='red'
                            isLoading={workingId === schedule.id}
                            onClick={() => runAction(schedule, 'cancel')}
                            size='xs'
                            variant='outline'
                          >
                            Cancel
                          </Button>
                        )}
                      </HStack>
                    </Td>
                  )}
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}
